import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, UserIcon, XIcon } from 'lucide-react';
import type { Lead } from '../data/mockData';
type NewLoanModalProps = {
  lead: Lead | null;
  onClose: () => void;
  onCreated: (loanName: string) => void;
};
type Form = {
  loanName: string;
  loanPurpose: string;
  propertyType: string;
  propertyUse: string;
  loanType: string;
  interestRate: string;
  loanAmount: string;
  appraisedValue: string;
  totalPayment: string;
  yearBuilt: string;
  docType: string;
  borrower: string;
  status: string;
  employmentTypes: string[];
  totalLoanAmount: string;
  firstTimeHomeBuyer: boolean;
  purchasePrice: string;
  percentDown: string;
  downPayment: string;
  loanProgram: string;
  fileOpenDate: string;
  smsUsed: string;
};
const NONE = '--None--';
const loanPurposes = [NONE, 'Purchase', 'Refinance', 'Cash-Out Refinance'];
const propertyTypes = [NONE, 'Single Family', 'Town House', 'Condo', '2-4 Unit', 'Manufactured'];
const propertyUses = [NONE, 'Primary Residence', 'Second Home', 'Investment'];
const loanTypes = [NONE, 'Conventional', 'FHA', 'VA', 'USDA', 'Non-QM'];
const docTypes = [NONE, 'Full Doc', 'Bank Statement', 'DSCR', 'Asset Depletion'];
const statuses = [NONE, 'Application Taken', 'Docs Signed', 'Approved w/ Conditions', 'Appraisal Back', 'CTC', 'Scheduled'];
const loanPrograms = [NONE, '30 Yr Fixed', '20 Yr Fixed', '15 Yr Fixed', '7/6 ARM', '5/6 ARM'];
const smsOptions = [NONE, 'Yes', 'No'];
const employmentOptions = ['W2', 'Self-Employed', 'Retired'];
const today = (): string => new Date().toISOString().slice(0, 10);
const prettyDate = (value: string): string => {
  if (!value) return '';
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, (month ?? 1) - 1, day ?? 1).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};
function Label({
  htmlFor,
  children,
  required
}: {
  htmlFor: string;
  children: React.ReactNode;
  required?: boolean;
}) {
  return <label htmlFor={htmlFor} className="mb-1 block text-[12px] font-medium text-ink" data-id="element-413" data-mp="12z0v4tjkit2v">
      {required && <span className="mr-0.5 text-danger" aria-hidden="true" data-id="element-414" data-mp="u05j30tueorn">
          *
        </span>}
      {children}
    </label>;
}
const fieldClass = 'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-[13px] text-ink placeholder:text-gray-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20';
export function NewLoanModal({
  lead,
  onClose,
  onCreated
}: NewLoanModalProps) {
  const [open, setOpen] = useState(true);
  const [certified, setCertified] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [form, setForm] = useState<Form>(() => ({
    loanName: '',
    loanPurpose: NONE,
    propertyType: NONE,
    propertyUse: NONE,
    loanType: 'Conventional',
    interestRate: '',
    loanAmount: '',
    appraisedValue: '',
    totalPayment: '$0.00',
    yearBuilt: '',
    docType: NONE,
    borrower: '',
    status: 'Application Taken',
    employmentTypes: [],
    totalLoanAmount: '',
    firstTimeHomeBuyer: false,
    purchasePrice: '',
    percentDown: '',
    downPayment: '0.00',
    loanProgram: NONE,
    fileOpenDate: today(),
    smsUsed: NONE
  }));
  useEffect(() => {
    if (!lead) return;
    setOpen(true);
    setCertified(false);
    setErrors([]);
    setForm(prev => ({
      ...prev,
      loanName: `${lead.name} Loan #:`,
      borrower: lead.name,
      loanPurpose: lead.loanPurpose?.startsWith('Refinance') ? 'Refinance' : 'Purchase',
      loanAmount: lead.amount > 0 ? `$${lead.amount.toLocaleString('en-US')}.00` : ''
    }));
  }, [lead]);
  useEffect(() => {
    if (!lead) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lead, onClose]);
  const set = <K extends keyof Form,>(key: K) => (value: Form[K]) => setForm(prev => ({
    ...prev,
    [key]: value
  }));
  const requiredMissing = useMemo(() => {
    const missing: string[] = [];
    if (!form.loanName.trim()) missing.push('Loan Name');
    if (form.loanPurpose === NONE) missing.push('Loan Purpose');
    if (form.propertyType === NONE) missing.push('Property Type');
    if (form.loanType === NONE) missing.push('Loan Type');
    if (!form.borrower.trim()) missing.push('Borrower Name');
    if (form.status === NONE) missing.push('Status');
    if (form.employmentTypes.length === 0) missing.push('Employment Type');
    if (form.loanProgram === NONE) missing.push('Loan Program (1st TD)');
    if (!form.fileOpenDate) missing.push('File Open Date');
    if (form.smsUsed === NONE) missing.push('SMS Used?');
    return missing;
  }, [form]);
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (requiredMissing.length > 0) {
      setErrors(requiredMissing);
      return;
    }
    onCreated(form.loanName);
    onClose();
  };
  const toggleEmployment = (option: string) => setForm(prev => ({
    ...prev,
    employmentTypes: prev.employmentTypes.includes(option) ? prev.employmentTypes.filter(item => item !== option) : [...prev.employmentTypes, option]
  }));
  return <AnimatePresence data-id="element-415" data-mp="7uebfxfc29zx">
      {lead && <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto p-4 sm:items-center" data-id="element-416" data-mp="14j3uhr2p58nh">
          <motion.button type="button" aria-label="Close" onClick={onClose} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="fixed inset-0 h-full w-full cursor-default bg-ink/40" data-id="element-417" data-mp="zjhg3eb4tmd3" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="new-loan-title" initial={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} transition={{
        type: 'spring',
        stiffness: 420,
        damping: 34
      }} className="relative flex max-h-[92vh] w-full max-w-[900px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl" data-id="element-418" data-mp="3g9xw481xuk3d">
            <header className="relative border-b border-gray-200 px-5 py-3.5" data-id="element-419" data-mp="37tp7yp4s5obo">
              <h2 id="new-loan-title" className="text-center text-[18px] font-semibold text-ink" data-id="element-420" data-mp="2tymungxlswou">
                New Loan
              </h2>
              <button type="button" onClick={onClose} aria-label="Close" className="absolute right-4 top-3 rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100" data-id="element-421" data-mp="2k3cqonnnaf8f">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-422" data-mp="2g0gcd95515aa" />
              </button>
            </header>

            <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col" data-id="element-423" data-mp="1weei80drwgqr">
              <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-5 py-4" data-id="element-424" data-mp="1wghidd8n3q97">
                <label className="flex cursor-pointer items-start gap-2.5" data-id="element-425" data-mp="1rho7ztongfd9">
                  <input type="checkbox" checked={certified} onChange={() => setCertified(prev => !prev)} className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-brand focus:ring-brand/30" data-id="element-426" data-mp="25gw5mi55zffc" />
                  <span className="text-[12.5px] leading-snug text-gray-700" data-id="element-427" data-mp="2adwcuwdr2o81">
                    I, the undersigned representative of United Mortgage Lending, LLC., do
                    hereby certify that I had verbal permission to pull the credit report for
                    the above referenced borrower(s) on {prettyDate(today())}
                  </span>
                </label>

                {errors.length > 0 && <div role="alert" className="mt-4 rounded-lg border border-danger/40 bg-danger-soft px-3.5 py-2.5" data-id="element-428" data-mp="2t32ra77igto1">
                    <p className="text-[12.5px] font-semibold text-danger" data-id="element-429" data-mp="998lk2uyue6d">
                      These required fields must be completed:
                    </p>
                    <p className="mt-0.5 text-[12px] text-danger" data-id="element-430" data-mp="iaou0528067y">{errors.join(', ')}</p>
                  </div>}

                <div className="mt-4 overflow-hidden rounded-lg border border-gray-200" data-id="element-431" data-mp="3hspvlguqsc25">
                  <button type="button" onClick={() => setOpen(prev => !prev)} aria-expanded={open} className="flex w-full items-center gap-2 bg-gray-50 px-5 py-3.5 text-left transition-colors hover:bg-gray-100" data-id="element-432" data-mp="3lszygkn8mmcs">
                    <ChevronDownIcon className={['h-4 w-4 text-brand transition-transform', open ? 'rotate-0' : '-rotate-90'].join(' ')} aria-hidden="true" data-id="element-433" data-mp="2y2m7kxge2tjs" />
                    <span className="font-display text-[14px] font-semibold text-navy" data-id="element-434" data-mp="2qk63s1v3veba">
                      Loan Information
                    </span>
                  </button>

                  {open && <div className="grid grid-cols-1 gap-x-10 gap-y-3.5 px-5 py-4 md:grid-cols-2" data-id="element-435" data-mp="2hkrjjk5j7zz8">
                      {/* Left column */}
                      <div className="space-y-3.5" data-id="element-436" data-mp="27slpm4vpz8he">
                        <div data-id="element-437" data-mp="27qsjcqp0qq9x">
                          <Label htmlFor="loanName" required data-id="element-438" data-mp="22titxb1mqem3">
                            Loan Name
                          </Label>
                          <input id="loanName" value={form.loanName} onChange={event => set('loanName')(event.target.value)} className={fieldClass} data-id="element-439" data-mp="1xw0jfg7yrrbz" />
                        </div>
                        <div data-id="element-440" data-mp="1ecso5vrqxk7i">
                          <Label htmlFor="loanPurpose" required data-id="element-441" data-mp="19fintl2tjo4w">
                            Loan Purpose
                          </Label>
                          <select id="loanPurpose" value={form.loanPurpose} onChange={event => set('loanPurpose')(event.target.value)} className={fieldClass} data-id="element-442" data-mp="1o3dk1bg1camq">
                            {loanPurposes.map(option => <option key={option} data-id="element-443" data-mp="15dxdo7ho5nye">{option}</option>)}
                          </select>
                        </div>
                        <div data-id="element-444" data-mp="10dj6ssrt0k1y">
                          <Label htmlFor="propertyType" required data-id="element-445" data-mp="2gt2ltr0kkepe">
                            Property Type
                          </Label>
                          <select id="propertyType" value={form.propertyType} onChange={event => set('propertyType')(event.target.value)} className={fieldClass} data-id="element-446" data-mp="2bsy5c0znvjz6">
                            {propertyTypes.map(option => <option key={option} data-id="element-447" data-mp="2ee5sx1jcx592">{option}</option>)}
                          </select>
                        </div>
                        <div data-id="element-448" data-mp="324s0krpg8nij">
                          <Label htmlFor="propertyUse" data-id="element-449" data-mp="37339n07x6tzp">Property Use</Label>
                          <select id="propertyUse" value={form.propertyUse} onChange={event => set('propertyUse')(event.target.value)} className={fieldClass} data-id="element-450" data-mp="3757crfxzvoyx">
                            {propertyUses.map(option => <option key={option} data-id="element-451" data-mp="3pumbh3q7k205">{option}</option>)}
                          </select>
                        </div>
                        <div data-id="element-452" data-mp="3vo940a48e5xf">
                          <Label htmlFor="loanType" required data-id="element-453" data-mp="3vp0zzm1qq5o8">
                            Loan Type
                          </Label>
                          <select id="loanType" value={form.loanType} onChange={event => set('loanType')(event.target.value)} className={fieldClass} data-id="element-454" data-mp="3qn1ykhpk9itx">
                            {loanTypes.map(option => <option key={option} data-id="element-455" data-mp="37xvi71c8p3ba">{option}</option>)}
                          </select>
                        </div>
                        <div data-id="element-456" data-mp="32xhbbmmdjzeu">
                          <Label htmlFor="interestRate" data-id="element-457" data-mp="2y0qx74u4rdw2">Interest Rate</Label>
                          <input id="interestRate" value={form.interestRate} onChange={event => set('interestRate')(event.target.value)} className={fieldClass} data-id="element-458" data-mp="2t14jyveaxxn9" />
                        </div>
                        <div data-id="element-459" data-mp="2sv3w077obtu0">
                          <Label htmlFor="loanAmount" data-id="element-460" data-mp="2svwu6jskhb0j">Loan Amount</Label>
                          <input id="loanAmount" value={form.loanAmount} onChange={event => set('loanAmount')(event.target.value)} className={fieldClass} data-id="element-461" data-mp="2xuq7oc7ut20p" />
                        </div>
                        <div data-id="element-462" data-mp="2a3b5qkjzompz">
                          <Label htmlFor="appraisedValue" data-id="element-463" data-mp="2a31b2jo01rdy">Appraised Value</Label>
                          <input id="appraisedValue" value={form.appraisedValue} onChange={event => set('appraisedValue')(event.target.value)} className={fieldClass} data-id="element-464" data-mp="2ewxa7pg01kap" />
                        </div>
                        <div data-id="element-465" data-mp="3j5nvife3019w">
                          <Label htmlFor="totalPayment" data-id="element-466" data-mp="3j6fix2xiysp3">Total Payment</Label>
                          <input id="totalPayment" value={form.totalPayment} onChange={event => set('totalPayment')(event.target.value)} className={fieldClass} data-id="element-467" data-mp="1pef1127u0sq" />
                        </div>
                        <div data-id="element-468" data-mp="6n7kfrvp2qry">
                          <Label htmlFor="yearBuilt" data-id="element-469" data-mp="6qbtb2k3uaoy">Year Built</Label>
                          <input id="yearBuilt" value={form.yearBuilt} onChange={event => set('yearBuilt')(event.target.value)} className={fieldClass} data-id="element-470" data-mp="kn8ikzkkrk3q" />
                        </div>
                        <div data-id="element-471" data-mp="2hi50ixr2xjoa">
                          <Label htmlFor="docType" data-id="element-472" data-mp="wy9hr14exkhv">Doc Type (1st TD)</Label>
                          <select id="docType" value={form.docType} disabled onChange={event => set('docType')(event.target.value)} className={`${fieldClass} cursor-not-allowed bg-gray-100 text-gray-400`} data-id="element-473" data-mp="x0c9pqp4lnp8">
                            {docTypes.map(option => <option key={option} data-id="element-474" data-mp="j6v1h21w3xei">{option}</option>)}
                          </select>
                        </div>
                      </div>

                      {/* Right column */}
                      <div className="space-y-3.5" data-id="element-475" data-mp="98xd8ek160lw">
                        <div data-id="element-476" data-mp="1ynlfvo2tk46q">
                          <Label htmlFor="borrower" data-id="element-477" data-mp="1yoelumtyepnt">Borrower Name</Label>
                          <div className="flex items-center gap-2 rounded-md border border-brand bg-white px-2 py-1.5 ring-2 ring-brand/20" data-id="element-478" data-mp="23mych0jhspmi">
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-navy text-white" data-id="element-479" data-mp="226u9ntchpln5">
                              <UserIcon className="h-3 w-3" aria-hidden="true" data-id="element-480" data-mp="1x895xjy14jty" />
                            </span>
                            <span id="borrower" className="min-w-0 flex-1 truncate text-[13px] text-ink" data-id="element-481" data-mp="1o5hvnngw3u3w">
                              {form.borrower}
                            </span>
                            <button type="button" onClick={() => set('borrower')('')} aria-label="Clear borrower" className="rounded p-0.5 text-brand transition-colors hover:bg-brand-soft" data-id="element-482" data-mp="2kxlgan53exxu">
                              <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-483" data-mp="33mia6pwkvofj" />
                            </button>
                          </div>
                        </div>
                        <div data-id="element-484" data-mp="ld5kntthot5f">
                          <Label htmlFor="status" required data-id="element-485" data-mp="ldxgn5r00sw8">
                            Status
                          </Label>
                          <select id="status" value={form.status} onChange={event => set('status')(event.target.value)} className={fieldClass} data-id="element-486" data-mp="qc8qhghan3eg">
                            {statuses.map(option => <option key={option} data-id="element-487" data-mp="19akzn287gda1">{option}</option>)}
                          </select>
                        </div>
                        <fieldset data-id="element-488" data-mp="1e3nz1qznygy5">
                          <legend className="mb-1 text-[12px] font-medium text-ink" data-id="element-489" data-mp="1i2woijno7snw">
                            <span className="mr-0.5 text-danger" aria-hidden="true" data-id="element-490" data-mp="1n29f9fhnxjvr">
                              *
                            </span>
                            Employment Type
                          </legend>
                          <div className="space-y-1.5" data-id="element-491" data-mp="12xdwuqllyepa">
                            {employmentOptions.map(option => <label key={option} className="flex cursor-pointer items-center gap-2 text-[13px] text-ink" data-id="element-492" data-mp="xykiyx2eslol">
                                <input type="checkbox" checked={form.employmentTypes.includes(option)} onChange={() => toggleEmployment(option)} className="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand/30" data-id="element-493" data-mp="17v5orkm960th" />
                                {option}
                              </label>)}
                          </div>
                        </fieldset>
                        <div data-id="element-494" data-mp="3smfyxph3h9l3">
                          <Label htmlFor="totalLoanAmount" data-id="element-495" data-mp="3sn8x421zmqrm">Total Loan Amount</Label>
                          <input id="totalLoanAmount" value={form.totalLoanAmount} onChange={event => set('totalLoanAmount')(event.target.value)} className={fieldClass} data-id="element-496" data-mp="3ihzj2w3m7sha" />
                        </div>
                        <div data-id="element-497" data-mp="54o1te5f783b">
                          <span className="mb-1 block text-[12px] font-medium text-ink" data-id="element-498" data-mp="54ebtqkdl0xm">
                            First Time Home Buyer
                          </span>
                          <label className="flex cursor-pointer items-center gap-2 text-[13px] text-ink" data-id="element-499" data-mp="3rbvzdua3maos">
                            <input type="checkbox" checked={form.firstTimeHomeBuyer} onChange={() => set('firstTimeHomeBuyer')(!form.firstTimeHomeBuyer)} className="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand/30" data-id="element-500" data-mp="5b4u0ku4kn0" />
                            Yes
                          </label>
                        </div>
                        <div data-id="element-501" data-mp="1bpm1veyi804g">
                          <Label htmlFor="purchasePrice" data-id="element-502" data-mp="1bsi074e9kqtf">Purchase Price</Label>
                          <input id="purchasePrice" value={form.purchasePrice} onChange={event => set('purchasePrice')(event.target.value)} className={fieldClass} data-id="element-503" data-mp="1gnxe1wc9umpu" />
                        </div>
                        <div data-id="element-504" data-mp="x1hi07nptik3">
                          <Label htmlFor="percentDown" data-id="element-505" data-mp="x0pyhv7e7ba6">Percent Down</Label>
                          <input id="percentDown" value={form.percentDown} onChange={event => set('percentDown')(event.target.value)} className={fieldClass} data-id="element-506" data-mp="j1qc6u7cr8h6" />
                        </div>
                        <div data-id="element-507" data-mp="3vm44nm0rj5m2">
                          <Label htmlFor="downPayment" data-id="element-508" data-mp="3vmvqi54k5oz5">Down Payment</Label>
                          <input id="downPayment" value={form.downPayment} onChange={event => set('downPayment')(event.target.value)} className={fieldClass} data-id="element-509" data-mp="ee8m54gps5sm" />
                        </div>
                        <div data-id="element-510" data-mp="ieiugnrzb8an">
                          <Label htmlFor="loanProgram" required data-id="element-511" data-mp="x3ozwthetuqz">
                            Loan Program (1st TD)
                          </Label>
                          <select id="loanProgram" value={form.loanProgram} onChange={event => set('loanProgram')(event.target.value)} className={fieldClass} data-id="element-512" data-mp="s5wy97qyqu9l">
                            {loanPrograms.map(option => <option key={option} data-id="element-513" data-mp="1e4jgki5dw0m9">{option}</option>)}
                          </select>
                        </div>
                        <div data-id="element-514" data-mp="1j22svciiu3bk">
                          <Label htmlFor="fileOpenDate" required data-id="element-515" data-mp="1num8ef0rr8j0">
                            File Open Date
                          </Label>
                          <input id="fileOpenDate" type="date" value={form.fileOpenDate} onChange={event => set('fileOpenDate')(event.target.value)} className={fieldClass} data-id="element-516" data-mp="1rvobj6xt44q9" />
                        </div>
                        <div data-id="element-517" data-mp="7he6eliztsp7">
                          <Label htmlFor="smsUsed" required data-id="element-518" data-mp="7i73svw2b5uo">
                            SMS Used?
                          </Label>
                          <select id="smsUsed" value={form.smsUsed} onChange={event => set('smsUsed')(event.target.value)} className={fieldClass} data-id="element-519" data-mp="chk0e8cqjrag">
                            {smsOptions.map(option => <option key={option} data-id="element-520" data-mp="2bzn4bwss47cm">{option}</option>)}
                          </select>
                        </div>
                      </div>
                    </div>}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 py-3" data-id="element-521" data-mp="2znn6rtzgdm71">
                <button type="button" onClick={onClose} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12.5px] font-medium text-ink transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand/30" data-id="element-522" data-mp="2upv2s1lja9mh">
                  Cancel
                </button>
                <button type="submit" className="rounded-lg bg-brand px-3.5 py-2 text-[12.5px] font-semibold text-white transition-colors hover:bg-brand-dark focus:outline-none focus:ring-2 focus:ring-brand/40" data-id="element-523" data-mp="3bxb597kp122b">
                  Save
                </button>
              </div>
            </form>
          </motion.div>
        </div>}
    </AnimatePresence>;
}